import { Table } from '@tanstack/react-table';
import { OrderStatus } from 'harmony-constants';
import moment from 'moment-timezone';
import { EstimatedTotalTripMode, Load, LocationGroup, Medication, Stop, User } from '../../../types';
import { filterStopByDestination, filterStopByOrigin } from '../../../utils/data-mapping-utils';
import { CARGO_COLUMN, DESTINATION_COLUMN, DRIVER_COLUMN, ORIGIN_COLUMN, STATUS_COLUMN, TRACTOR_COLUMN, TRAILER_COLUMN } from '../hooks/use-draft-load-columns';
import { GlobalFilter, GlobalFilterItem } from '../hooks/use-draft-load-table';

export const isLoadRowEditable = (isRowExpanded: boolean, load: Load) => {
    return isRowExpanded && load.status !== OrderStatus.Delivered;
}

const presetableColumns = [
    ORIGIN_COLUMN,
    DESTINATION_COLUMN,
    CARGO_COLUMN,
    DRIVER_COLUMN,
    TRACTOR_COLUMN,
    TRAILER_COLUMN,
    STATUS_COLUMN,
]

export const anyColumnFiltered = (table: Table<Load>) => {
    return table.getAllColumns().map(x => x.getIsFiltered()).some(x => !!x);
}

export const anyPresetableColumnFiltered = (table: Table<Load>) => {
    return getPresetableColumns(table).filter(x => x.getIsFiltered()).some(x => !!x);
}

export const getPresetableColumns = (table: Table<Load>) => {
    return table.getAllColumns().filter(x => presetableColumns.includes(x.id));
}

export const getPageSize = (isDrafts: boolean, user: User) => {
    const preferencesKey = isDrafts ? 'drafts' : 'loads';
    const pageSize = user.preferences?.tables?.[preferencesKey]?.pageSize || 100;
    return pageSize;
}

export const globalFilterRemover = (table: Table<Load>, field: GlobalFilterItem['type']) => {
    table.setGlobalFilter((currentGlobalFilter: GlobalFilter) => {
        const newFilter = currentGlobalFilter?.filter((item: GlobalFilterItem) => item.type !== field);
        return newFilter?.length ? newFilter : undefined;
    });
}

export const removeDrawerFilters = (table: Table<Load>) => {
    table.setGlobalFilter((currentGlobalFilter: GlobalFilter) => {
        const newFilter = currentGlobalFilter?.filter((item: GlobalFilterItem) => ['arbitraryTextFilter', 'selectedFilter'].includes(item.type));
        return newFilter?.length ? newFilter : undefined;
    });
}

export const getActiveDrawerFilters = (table: Table<Load>) => {
    const globalFilter: GlobalFilter = table.getState().globalFilter;
    return globalFilter?.filter((filterItem: GlobalFilterItem) => !['arbitraryTextFilter', 'selectedFilter'].includes(filterItem.type));
}

export const showSelectedOnly = (table: Table<Load>) => {
    table.setGlobalFilter((currentGlobalFilter: GlobalFilter): GlobalFilter => ([
        ...(currentGlobalFilter || []), {
            type: 'selectedFilter'
        },
    ]));
}

export const gridTemplateColumnsGenerator = (
    isDrafts: boolean,
    showLoadDate: boolean,
    estimatedTotalTripMode: EstimatedTotalTripMode,
    showTractor: boolean,
    showTrailer: boolean,
) => {
    const gridTemplateColumns = [
        // 3 buttons at 24px = 72 (no horizontal margin/padding)
        '72px', // selection & buttons (edit/unmerge on drafts - edit/delivery log on loads)
        ...(showLoadDate ? ['1fr'] : []), // load date
        '.85fr', // order number
        '1.5fr', // cargo
        '1.5fr', // origin
        '1.5fr', // destination
        '1fr', // first stop
        '1fr', // last stop
        ...(isDrafts ? [] : ['1fr']), // completed at
        ...(estimatedTotalTripMode != 'none' ? ['.5fr'] : []), // total trip length (time or distance)
        '20px', // resource scheduling button
        '.85fr', // driver
        ...(showTractor ? ['.85fr'] : []), // tractor
        ...(showTrailer ? ['.85fr'] : []), // trailer
        '.5fr', // status
        '.4fr', // expand
    ];

    return gridTemplateColumns.join(' ');
}

export const stopGridTemplateColumnsGenerator = (isDrafts: boolean, isEditable: boolean) => {
    const gridTemplateColumns = [
        ...(isEditable ? ['30px'] : []),
        '1fr', // stop type
        '1.25fr', // cargo type
        '2fr', // location
        '.9fr', // sublocations
        '1.5fr', // scheduled
        ...(isEditable ? ['30px'] : []), // auto-calc
        ...(isDrafts ? [] : ['1.25fr']), // completedAt
        '.75fr', // units
        ...(isDrafts ? [] : ['.75fr']), // actual units
        '30px',
        ...(isEditable ? ['30px'] : []), // split
        '1.25fr', // order number
        '1.25fr', // production plan
        '60px', // notes
        '30px', // expander
        ...(isEditable ? ['30px'] : []), // delete
    ];

    return gridTemplateColumns.join(' ');
}

export const genOrderNumbers = (load: Load) => {
    return [...new Set(load.stops.map(s => s.orderNumber).filter(x => x !== null))];
}

export const genOriginIds = (load: Load) => {
    return load.stops.filter(filterStopByOrigin).map(x => x.organizationLocationId);
}

export const genDestinationIds = (load: Load) => {
    return load.stops.filter(filterStopByDestination).map(x => x.organizationLocationId);
}

export const getEarliestArrivalTime = (stops: Stop[]) => {
    const sortedByArrivalStops = stops.toSorted((a, b) => moment(a.arrivalTime).diff(moment(b.arrivalTime)));

    return sortedByArrivalStops[0]?.arrivalTime;
}

export const findEntityColor = (id: number | null, colorEntities: LocationGroup[] | Medication[]) => {
    const assignedGroup = colorEntities.find(g => g.id === id);
    return assignedGroup ? assignedGroup.hexColor : '';
}

export const getSingleColorTagStyle = (id: number | null, colorEntities: LocationGroup[] | Medication[]) => {
    const hexColor = findEntityColor(id, colorEntities);
    return hexColor
            ? { paddingLeft: '2px', borderLeft: `4px solid ${hexColor}` }
            : { paddingLeft: `6px` };
}

export const getMedicationsTagStyle = (medicationIds: number[], medications: Medication[]) => {
    const hexColors = medicationIds?.map(id => medications.find(med => med.id === id)?.hexColor)
        .filter(color => color !== undefined)
        .distinctBy<string, string>(color => color) || [];

    // Early return if no medication colors found
    if (!hexColors.length) { return { paddingLeft: '6px' } }

    const quotient = Math.floor(100 / hexColors.length);

    const gradientValues = hexColors.map((color, index) => {
        if (index === 0) {
            return `${color} ${(index + 1) * quotient}%`;
        } else if (index < hexColors.length - 1) {
            return `${color} ${index * quotient}%,${color} ${(index + 1) * quotient}%`;
        } else {
            return `${color} ${index * quotient}%`;
        }
        
    });
    const colorGradient = `linear-gradient(${gradientValues.join(',')}) 1`;

    return { paddingLeft: '2px', borderLeft: `4px solid`, borderImage: colorGradient }
}

export const setExpandedRows = (table: Table<Load>, rowIds: number[]) => {
    const expandedRows = rowIds.reduce((acc: Record<string, boolean>, id) => {
        acc[String(id)] = true;
        return acc;
    }, {});
    table.setExpanded(expandedRows);
}